import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type InquiryItem = {
  productHandle: string;
  productName: string;
  categoryName?: string;
  imageUrl?: string;
  quantity: number;
  note?: string;
};

type Ctx = {
  items: InquiryItem[];
  add: (item: Omit<InquiryItem, "quantity"> & { quantity?: number }) => void;
  remove: (handle: string) => void;
  setQuantity: (handle: string, qty: number) => void;
  setNote: (handle: string, note: string) => void;
  clear: () => void;
  count: number;
};

const InquiryContext = createContext<Ctx | null>(null);
const STORAGE_KEY = "qtx_inquiry_v1";

export function InquiryProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<InquiryItem[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch {}
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items, hydrated]);

  const value: Ctx = {
    items,
    count: items.reduce((sum, it) => sum + it.quantity, 0),
    add: (item) =>
      setItems((curr) => {
        const existing = curr.find((c) => c.productHandle === item.productHandle);
        if (existing) {
          return curr.map((c) =>
            c.productHandle === item.productHandle
              ? { ...c, quantity: c.quantity + (item.quantity ?? 1) }
              : c,
          );
        }
        return [...curr, { ...item, quantity: item.quantity ?? 1 }];
      }),
    remove: (handle) => setItems((curr) => curr.filter((c) => c.productHandle !== handle)),
    setQuantity: (handle, qty) =>
      setItems((curr) =>
        curr.map((c) => (c.productHandle === handle ? { ...c, quantity: Math.max(1, qty) } : c)),
      ),
    setNote: (handle, note) =>
      setItems((curr) => curr.map((c) => (c.productHandle === handle ? { ...c, note } : c))),
    clear: () => setItems([]),
  };

  return <InquiryContext.Provider value={value}>{children}</InquiryContext.Provider>;
}

export function useInquiry() {
  const ctx = useContext(InquiryContext);
  if (!ctx) throw new Error("useInquiry must be used inside InquiryProvider");
  return ctx;
}
